import pick from 'lodash/pick';
import omit from 'lodash/omit';
import set from 'lodash/set';
import get from 'lodash/get';
import merge from 'lodash/merge';
import isEmpty from 'lodash/isEmpty';
import union from 'lodash/union';

import {message} from 'antd';
import dayjs from 'dayjs';

import {selectCurrentJourneyId} from '@/models/journey';
import {processAPIResponse} from '@/utils/request';
import * as LeadSelectors from '@/selectors/leadSelectors';
import {
  createCSVLeadsRequest,
  createLeadRequest,
  createLeadWithoutSource,
  editLeadsRequest,
  fetchAssignees,
  fetchColdProspectsOverviewRequest,
  fetchLeads,
  fetchLinkedLeadsRequest,
  fetchSingleLeadDetails,
} from '@/services/api/lead';

import {fetchLeadTasksList} from '@/services/api/leadTask';
import {withProjectTasks} from '@/utils/task';
import * as PreferencesSelectors from '@/selectors/preferencesSelectors';
import {fetchMeRequest} from '@/services/api/user';
import {parseSort} from '@/utils/functions';
import {FILTER_PLACES} from '@/components/Filters/LeadFilters';
import {LeadModel} from '@/typings/models/Lead';
import {GROUPS_TO_BE_LOADED, LEAD_VIEWS} from '@/components/Lead/constants';
import {formatCustomerDotNotationObject, getLocalEditPaths, prepareLeadForLocalEdit} from '@/utils/lead';
import type {RecentLocalEdit} from '@/utils/lead';
import {updateEntityExtraField} from '@/utils/extraFields';

// How long a local edit shields its fields from `lead_updated` websocket echoes
// (see reconcileWebsocketLead); covers the PATCH round trip plus queue latency.
const LOCAL_EDIT_PROTECTION_MS = 30_000;

// The API's maximum; a project rarely has more tasks than this.
const PROJECT_TASKS_PAGE_SIZE = 500;


function* fetchLeadsEffect(payload, { select, call, put }) {
  const filters = yield select(state => PreferencesSelectors.selectFilters(state, payload.view === LEAD_VIEWS.KANBAN ? FILTER_PLACES.LEAD_BOARD : FILTER_PLACES.LEAD_LIST));
  const preferences = yield select(state => PreferencesSelectors.selectPlacePreferences(state, payload.view === LEAD_VIEWS.KANBAN ? FILTER_PLACES.LEAD_BOARD : FILTER_PLACES.LEAD_LIST));
  const shownProperties = yield select(payload.view === LEAD_VIEWS.TABLE ? PreferencesSelectors.selectShownLeadsTableProperties : PreferencesSelectors.selectShownLeadsProperties);
  const extraFieldsToShow = shownProperties.filter(property => property.startsWith('extraField:')).map(property => property.split('extraField:')[1]);
  const filteredObj = Object.fromEntries(
    Object.entries(filters).filter(([key, value]) => value !== null),
  );
  if (filteredObj.sort) {
    const { sort_by, sort_direction } = parseSort(filteredObj.sort);
    if (sort_by && sort_direction) {
      delete filteredObj.sort;
      filteredObj.sort_by = sort_by;
      filteredObj.sort_direction = sort_direction;
    }
  }
  if (payload.view === LEAD_VIEWS.KANBAN) {
    filteredObj.group_by = 'lead_state';
    filteredObj.groups_to_be_loaded = GROUPS_TO_BE_LOADED.ALL;
    delete filteredObj.lead_state;
  } else if (payload.view === LEAD_VIEWS.TABLE) {
    if (filteredObj.table_group_by) {
      filteredObj.groups_to_be_loaded = GROUPS_TO_BE_LOADED[filteredObj.table_group_by] || GROUPS_TO_BE_LOADED.ALL;
      filteredObj.group_by = filteredObj.table_group_by;
      delete filteredObj.table;
    } else {
      filteredObj.page_size = window.innerHeight > 800 ? 30 : 20;
    }
  }

  filteredObj.is_archived = filteredObj.is_archived ? '1' : '0';
  if (extraFieldsToShow.length > 0) {
    filteredObj.with_extra_fields = extraFieldsToShow.join(',');
  }
  if (!isEmpty(filteredObj.date)) {
    const dateFilterType = preferences?.dateFilterType?.value || 'created';
    filteredObj[`${dateFilterType}_from`] = dayjs(filteredObj.date?.start).format('YYYY-MM-DD');
    filteredObj[`${dateFilterType}_to`] = dayjs(filteredObj.date?.end).format('YYYY-MM-DD');
  }
  delete filteredObj.date;

  const query = { ...filteredObj, ...payload, with: 'latest_appointment' };
  const journeyId = yield select(selectCurrentJourneyId);
  const { response, data } = yield call(fetchLeads, { params: { journeyId }, url: payload.url, query });
  yield processAPIResponse({ response, data }, {
    * onSuccess(data) {
      yield put({
        type: 'saveLeads',
        payload: data,
        options: query,
      });
    },
  });
}

const initialState = {
  leadFields: {
    default: [],
    meta: [],
  },
  list: {},
  details: {},
  projectTasks: {},
  lead: {
    drawer: {
      visible: false,
    },
    status: {},
  },
  leadSourceList: [],
  serviceList: [],
  leadStatusList: [],
  unassignedLeadsList: [],
  leadDetailsDrawer: null,
  assignmentDrawerVisible: false,
  addLead: {
    drawer: {
      visible: false,
    },

  },
  leadSourceDetail: { lead_source: {} },
  kanbanItems: [],
  kanbanItemsColumnsIncrements: {},
  recentLocalEdits: {},

};
export default {
  namespace: 'leads',

  state: initialState,

  effects: {
    * fetchLeadsByUrl({ payload }, { call, put }) {
      const { response, data } = yield call(fetchLeads, { url: payload.url });
      if (payload.then) payload.then();
      yield processAPIResponse({ response, data }, {
        * onSuccess(data) {
          yield put({
            type: 'saveLeads',
            payload: data,
            options: payload,
          });
        },
      });
    },
    * lazyLoadMoreGroups(_, { call, put, select, all }) {
      const groupedList = yield select(LeadSelectors.selectTableData);
      const newGroupsToBeLoaded = groupedList?.data?.filter(group => !group.is_loaded && group?.links?.first).slice(0, 5) || [];
      if (isEmpty(newGroupsToBeLoaded)) return

      yield all(
        newGroupsToBeLoaded.map(group =>
          put.resolve({
            type: 'fetchLeadsByUrl',
            payload: {
              url: group.links.first,
              groupId: group.filter_id,
              group_by: 'lead_state',
            },
          }),
        ),
      );
    },
    fetch: [
      function* ({ payload = {} }: { payload: { search?: string, url?: string, order_by?: string } }, {
        call,
        put,
        select,
      }) {
        yield fetchLeadsEffect(payload, { select, call, put });
      },
      {
        type: 'takeLatest',
      },
    ],
    fetch_Silent: [
      function* ({ payload = {} }: { payload: { search?: string, url?: string, order_by?: string } }, {
        call,
        put,
        select,
      }) {
        yield fetchLeadsEffect(payload, { select, call, put });
      },
      {
        type: 'takeLatest',
      },
    ],
    * fetchColdProspectsOverview({ payload = {} }: {
      payload: { search?: string, url?: string, order_by?: string }
    }, { call, put, select }) {
      const { response, data } = yield call(fetchColdProspectsOverviewRequest);
      yield processAPIResponse({ response, data }, {
        * onSuccess(data) {
          yield put({
            type: 'saveColdProspectsOverview',
            payload: data,
          });
        },
      }, 'data');
    },
    * fetchSingleLead({ payload = {} }: { payload: { search?: string, url?: string } }, { call, put, select }) {
      const { leadId } = payload;
      const FetchSingleLeadDetailsResponse = yield call(fetchSingleLeadDetails, { params: { leadId } });
      yield processAPIResponse(FetchSingleLeadDetailsResponse, {
        * onSuccess(data) {
          yield put({
            type: 'saveLeadDetails',
            payload: data,
          });
          yield put({ type: 'fetchProjectTasks', payload: { leadId } });
        },
        * onError(data) {
          message.error(data?.error?.message || data?.message);
          yield put({
            type: 'toggleLeadDetailsDrawer',
          });
        },
      }, 'data');
    },
    * fetchProjectTasks({ payload }: { payload: { leadId: string } }, { call, put }) {
      const { leadId } = payload;
      if (!leadId) return;
      const response = yield call(fetchLeadTasksList, { query: { lead_id: leadId, page_size: PROJECT_TASKS_PAGE_SIZE } });
      yield processAPIResponse(response, {
        * onSuccess(tasks) {
          yield put({ type: 'saveProjectTasks', payload: { leadId, tasks: tasks || [] } });
        },
        * onError() {},
      }, 'data');
    },
    * fetchLinkedLeads({ payload = {} }: { payload: { search?: string, url?: string } }, { call, put, select }) {
      const { leadId } = payload;

      const FetchLinkedLeadsResponse = yield call(fetchLinkedLeadsRequest, { params: { leadId } });
      yield processAPIResponse(FetchLinkedLeadsResponse, {
        * onSuccess(data) {
          if (isEmpty(data)) return;
          yield put({
            type: 'saveLinkedLeads',
            payload: {
              data,
              leadId,
            },
          });
        },
      }, 'data');
    },
    * fetchUnassignedLeads({ payload = {} }: { payload: { search?: string, url?: string } }, { call, put, select }) {
      const query = { assignee: 'null', sort_by: 'firstname', sort_direction: 'desc', ...payload };
      const { response, data } = yield call(fetchLeads, { url: payload.url, query });
      yield processAPIResponse({ response, data }, {
        * onSuccess(data) {
          yield put({
            type: 'saveUnassignedLeads',
            payload: data,
          });
        },
      });
    },
    * edit({ payload = {}, then, previousState }: { payload: any }, { call, put, select }) {
      const leadId = payload.leadId;
      let lead = yield select(state => LeadSelectors.selectLeadData(state, leadId));
      let reduxState = yield select(state => state);
      const previousLeadState = lead?.lead_state?.id;
      yield put({
        type: 'updateLeadInStore',
        payload: prepareLeadForLocalEdit(payload, reduxState),
      });
      const localEditPaths = getLocalEditPaths(payload);
      yield put({
        type: 'markRecentLocalEdit',
        payload: { leadId, paths: localEditPaths },
      });

      const params = pick(payload, ['leadId', 'applyLate']);
      const query = payload?.query;
      const toEdit = omit(payload, ['leadId', 'applyLate', 'leadOverride', 'query', 'assignee', 'fetchDetails']);
      lead = yield select(state => LeadSelectors.selectLeadData(state, params.leadId));

      const fromLeadStateId = previousLeadState || previousState;
      const toLeadStateId = payload.lead_state_id;

      const body = formatCustomerDotNotationObject(toEdit);
      const { response, data } = yield call(editLeadsRequest, { params, data: body, query });

      if (fromLeadStateId !== toLeadStateId) {
        yield put({
          type: 'moveLeadBetweenColumns',
          payload: {
            leadId: lead.id,
            fromColumnId: `column-${fromLeadStateId}`,
            toColumnId: `column-${toLeadStateId}`,
          },
        });
      }
      yield processAPIResponse({ response, data }, {
        * onSuccess(data) {
          yield put({
            type: 'editLeadDetails',
            payload: { lead: data },
          });
          yield put({
            type: 'markRecentLocalEdit',
            payload: { leadId, paths: localEditPaths, modified: data?.modified },
          });

          if (then)
            then(data);
          if (payload?.fetchDetails) {
            yield put({
              type: 'fetchSingleLead',
              payload: {
                leadId: lead.id,
              },
            });
          }

        },
      }, 'data');
    },
    * fetchAssignees({ payload = {} }: { payload: { search?: string, url?: string } }, { call, put, select }) {
      yield put({
        type: 'setAgentsInitialized',
        payload: true,
      });
      const AllUsersAPICall = yield call(fetchAssignees, { query: { in_scope: 1, page_size: 50 } });
      const usersList = AllUsersAPICall?.data?.data;

      const ActiveUsersAPICall = yield call(fetchAssignees, {
        query: {
          in_scope: 1,
          active: 'only_active',
          page_size: 50,
        },
      });
      const activeUsersList = ActiveUsersAPICall?.data?.data;

      const AssignableUsersAPICall = yield call(fetchAssignees, {
        query: {
          in_scope: 1,
          only_assignable: 1,
          active: 'only_active',
          page_size: 50,
        },
      });
      const assignableUsersList = AssignableUsersAPICall?.data?.data;

      yield put({
        type: 'setAssignees',
        payload: {
          allAgentsList: usersList || [],
          activeAgentsList: activeUsersList || [],
          assignableAgentsList: assignableUsersList || [],
        },
      });

    },
    * create({ payload, then }, { call, put, select }) {
      let APICall;
      if (payload.sourceId) {
        APICall = yield call(createLeadRequest, {
          params: { sourceId: '8klAno49' },
          data: [{
            ...payload.data,
            priority: 0,
            meta: [],
          }],
        });

      } else {
        APICall = yield call(createLeadWithoutSource, {
          data: {
            ...payload.data,
            priority: 0,
            meta: [],
          },
        });
      }
      const { response, data } = APICall;
      yield processAPIResponse({ response, data }, {
        * onSuccess(data) {
          yield put({
            type: 'toggleAddLeadsDrawer',
          });
          yield put({
            type: 'fetch',
          });

          payload.intl && message.success(payload.intl.formatMessage({
            id: 'pages.leads.createLeadSuccess',
            defaultMessage: 'Lead created successfully',
          }));
          yield put({
            type: 'saveLeadsData',
            list: data.leads,
          });
          if (then) then(data.leads);

          //   TODO: Add to leads list
        }
        , onError(error) {
          message.error(error?.error?.message || payload.intl.formatMessage({
            id: 'pages.leads.createLeadFailed',
            defaultMessage: 'Creating lead failed',
          }));
        },
      }, 'data');

    },

    * fetchMe({ payload = {} }, { call, put, select }) {
      const { response, data } = yield call(fetchMeRequest);
      yield processAPIResponse({ response, data }, {
        * onSuccess(data) {
          yield put({
            type: 'journey/gotJourneys',
            payload: data?.Journeys,
          });
        },
      });
    },

    * createCSVLeads({ payload }, { call, put, select }) {
      const callback = payload?.cb;
      payload = {
        ...payload.allData,
        lead_state_id: payload.allData?.incoming_lead_state_id,
        leads: payload.mappedNestedData,
        sourceId: payload.sourceId,
      };
      const { response, data } = yield call(createCSVLeadsRequest, {
        data: { ...payload },
        params: {
          sourceId:
          payload?.sourceId,
        },
      });
      yield processAPIResponse({ response, data }, {

        * onSuccess(data) {
          message.success('Leads imported successfully');
          callback && callback(null, true);

          yield put({
            type: 'fetch',
          });
          yield put({
            type: 'fetchMe',
          });
        }
        , onError(error) {
          callback && callback(error, false);

          message.error(error?.error?.message);

        },
      });


    },


  },

  reducers: {
    saveLeads(state, action) {
      let newList = action.payload;
      const isPaged = action?.options?.concat || action?.options?.url;
      const STATE_KEY = action?.options?.view === LEAD_VIEWS.KANBAN ? 'groupedList' : 'tableData';
      const leads = state.leads || {};
      // Kanban pagination
      if (action?.options?.group_by || action?.options?.url) {
        if (!isPaged) {
          newList.data.forEach(list => {
            list.data.forEach(lead => {
              leads[lead.id] = lead;
            });
          });
          const newGroupedList = newList.data.map(groupedLeads => ({
            ...groupedLeads,
            data: groupedLeads.data.map(lead => lead.id),
          }));

          return {
            ...state,
            leads,
            [STATE_KEY]: {
              updatedAt: Date.now(),
              data: newGroupedList,
            },
          };
        }
        newList.data.forEach(lead => {
          leads[lead.id] = lead;
        });

        return {
          ...state,
          leads,
          [STATE_KEY]: {
            ...(state[STATE_KEY] || {}),
            updatedAt: Date.now(),
            data: state[STATE_KEY].data.map(groupedLead => {
              if (groupedLead.filter_id === action.options.groupId) {
                return {
                  ...groupedLead,
                  ...newList,
                  data: Array.from(new Set(groupedLead.data.concat(newList.data.map(lead => lead.id)))),
                };
              }
              return groupedLead;
            }),
          },
        };

      }
      newList.data.forEach(lead => {
        leads[lead.id] = lead;
      });

      // Table pagination
      if (isPaged) {
        newList = {
          ...newList,
          ...state.list,
          ...action.payload,
          data: state.list.data.concat(newList.data.map(lead => lead.id)),
        };
        return {
          ...state,
          leads,
          list: newList,
        };
      }

      return {
        ...state,
        leads,
        list: {
          ...newList,
          updatedAt: Date.now(),
          data: newList.data.map(lead => lead.id),
        },
      };
    },
    saveUnassignedLeads(state, action) {
      return {
        ...state,
        unassignedLeadsList: action.payload,
      };
    },

    saveLeadsData(state, action) {
      const leads = action.payload?.list || [];
      const newLeads = {};
      leads.forEach(lead => {
        newLeads[lead.id] = lead;
      });

      return {
        ...state,
        leads: {
          ...state.leads,
          ...(newLeads || {}),
        },
      };
    },
    saveLinkedLeads(state, action) {
      const { leadId, data } = action.payload;
      const lead = state.details?.[leadId] || {};
      return {
        ...state,
        details: {
          ...state.details,
          [leadId]: {
            ...lead,
            linkedLeads: data,
          },
        },
      };
    },
    saveProjectTasks(state, action) {
      const { leadId, tasks } = action.payload;
      const lead = state.details?.[leadId];
      return {
        ...state,
        projectTasks: { ...state.projectTasks, [leadId]: tasks },
        ...(lead ? { details: { ...state.details, [leadId]: withProjectTasks(lead, tasks) } } : {}),
      };
    },
    saveLeadDetails(state, action) {
      const projectTasks = state.projectTasks?.[action.payload.id];
      const payload = projectTasks ? withProjectTasks(action.payload, projectTasks) : action.payload;
      const merged = {
        ...omit(get(state.details, action.payload.id, {}), ['customer_extra_fields','result_extra_fields']),
        ...payload,
      };
      const toUpdate = {
        details: {
          ...state.details,
          [action.payload.id]: merged,
        },
      };
      if (!state.leads)
        state.leads = {};

      if (!state.leads[action.payload.id]) {
        toUpdate.leads = {
          ...state.leads,
          [action.payload.id]: merged,
        };
      }
      return {
        ...state,
        ...toUpdate,
      };
    },
    updateLeadExtraField(state, action) {
      const { leadId, extraFieldTypeId, value, fieldType } = action.payload;
      if (!leadId) return state;

      const lead = state.leads?.[leadId] || {};
      const newLead = updateEntityExtraField(lead, extraFieldTypeId, value, fieldType);

      return {
        ...state,
        leads: {
          ...state.leads,
          [leadId]: newLead,
        },
      };
    },
    updateLeadInStore(state, action) {
      const { leadId } = action.payload;

      const mergeLead = (lead: LeadModel) => {
        const toOverride = { ...action.payload, ...(action.payload.leadOverride || {}) };
        Object.keys(omit(toOverride, ['leadId'])).forEach(key => {
          set(lead, key, toOverride[key]);
        });
        lead.lastEditAt = new Date().toISOString();
        return lead;
      };

      const toUpdate: any = {};

      const prevLead = state.leads[leadId];
      if (prevLead) {
        const newLead = mergeLead(prevLead);
        toUpdate.leads = {
          ...state.leads,
          [leadId]: {
            ...newLead,

          },
        };
      }

      const prevLeadDetails = state.details[leadId];
      if (prevLeadDetails) {
        const newLeadDetails = mergeLead(prevLeadDetails);
        toUpdate.details = {
          ...state.details,
          [leadId]: {
            ...newLeadDetails,

          },
        };
      }

      return {
        ...state,
        ...toUpdate,
      };
    },
    editLeadDetails(state, action) {
      const { lead } = action.payload;
      const toUpdate: any = {
        leads: {
          ...state.leads,
          [lead.id]: merge({}, state.leads[lead.id] || {}, lead),
        },
      };

      // The details drawer renders from state.details first, so the
      // server-confirmed lead must land there too — otherwise the drawer
      // keeps stale values (e.g. lead_state) whenever the optimistic
      // updateLeadInStore pass missed.
      if (state.details?.[lead.id]) {
        toUpdate.details = {
          ...state.details,
          [lead.id]: merge({}, state.details[lead.id], lead),
        };
      }

      return {
        ...state,
        ...toUpdate,
      };
    },
    toggleAssigneeDrawer(state, { payload }) {
      return {
        ...state,
        assignmentDrawerVisible: !state.assignmentDrawerVisible,
      };
    },
    toggleLeadDetailsDrawer(state, { payload }) {
      const lead = get(payload, 'lead');
      return {
        ...state,
        leadDetailsDrawer: {
          leadId: lead?.id,
          toggledAt: new Date().getTime(),
          ...omit(payload, ['lead']),
        },
      };
    },
    setAssignees(state, action) {
      const {
        payload,
      } = action;

      return {
        ...state,
        ...payload,
      };
    },
    toggleAddLeadsDrawer(state, { payload = {} }) {
      const addLead = { ...state.addLead, drawer: { visible: !state.addLead?.drawer?.visible, ...payload } };
      return {
        ...state,
        addLead,
      };
    },
    saveLeadSourceDetail(state, action) {
      return {
        ...state,
        leadSourceDetail: action.payload,
      };
    },
    reset(state) {
      return initialState;
    },
    saveColdProspectsOverview(state, action) {
      return {
        ...state,
        coldProspectsOverview: action.payload,
      };
    },
    updateKanbanItems(state, action) {
      const { items, options } = action.payload;
      const newState = {
        ...state,
        kanbanItems: items || [],
      };
      if (options.resetIncrements)
        newState.kanbanItemsColumnsIncrements = {};
      return newState;
    },
    clearTableData(state, action) {
      return {
        ...state,
        tableData: {},
      };
    },
    setAgentsInitialized(state, action) {
      return {
        ...state,
        agentsInitialized: action.payload,
      };
    },
    markRecentLocalEdit(state, action) {
      const { leadId, paths, modified } = action.payload;
      const now = Date.now();
      const recentLocalEdits: Record<string, RecentLocalEdit> = Object.fromEntries(
        Object.entries<RecentLocalEdit>(state.recentLocalEdits || {}).filter(([, edit]) => edit.expiresAt > now),
      );
      const previous = recentLocalEdits[leadId];
      return {
        ...state,
        recentLocalEdits: {
          ...recentLocalEdits,
          [leadId]: {
            paths: union(previous?.paths || [], paths),
            modified,
            expiresAt: now + LOCAL_EDIT_PROTECTION_MS,
          },
        },
      };
    },
    moveLeadBetweenColumns(state, action) {
      const { leadId, fromColumnId, toColumnId } = action.payload;
      const currentItems = { ...state.kanbanItems };

      if (!currentItems[fromColumnId] || !currentItems[toColumnId]) {
        return state;
      }

      const fromColumnLeads = [...currentItems[fromColumnId]];
      const leadIndex = fromColumnLeads.indexOf(leadId);

      if (leadIndex === -1) {
        return state;
      }

      fromColumnLeads.splice(leadIndex, 1);

      const toColumnLeads = [...currentItems[toColumnId]];
      toColumnLeads.push(leadId);

      return {
        ...state,
        kanbanItemsColumnsIncrements: {
          ...(state.kanbanItemsColumnsIncrements || {}),
          [fromColumnId]: (state.kanbanItemsColumnsIncrements[fromColumnId] || 0) - 1,
          [toColumnId]: (state.kanbanItemsColumnsIncrements[toColumnId] || 0) + 1,
        },
        kanbanItems: {
          ...currentItems,
          [fromColumnId]: fromColumnLeads,
          [toColumnId]: toColumnLeads,
        },
      };
    },
  },
};


/*
* // region Magic Fields
        const statusSettings = yield select(LeadStateSelectors.selectStates)
        const leadState = statusSettings.find(leadStatus => leadStatus.id === payload.lead_state_id)
        const magicFields = extractMagicFieldsFromLeadState(leadState)
        if (magicFields?.optional || magicFields?.mandatory) {
          yield put({
            type: 'openMagicFieldsEditor',
            payload: {
              magicFields,
              leadId: lead.id
            }
          })
        }
        // endregion
        *
        *  openMagicFieldsEditor(state, action) {
      return {
        ...state,
        magicFieldEditor: action.payload
      }
    },
    closeMagicFieldsEditor(state, action) {
      return {
        ...state,
        magicFieldEditor: action.payload
      }
    },
* */
